import React from 'react';
import { Link } from 'react-router-dom';
import { Calendar, MapPin, ExternalLink, Sparkles, Users2, Download, Eye } from 'lucide-react';
import { communityData } from '../data/community';
import { BilingualFlyer } from './BilingualFlyer';

const categoryColors = {
  Event: { bg: 'bg-rose-500', text: 'text-white' },
  Community: { bg: 'bg-violet-500', text: 'text-white' },
  Advocacy: { bg: 'bg-amber-500', text: 'text-white' }
};

const CommunitySpotlightSection = () => {
  return (
    <section id="community" className="py-24 bg-slate-50 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 bg-violet-200/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-teal-200/30 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-flex items-center gap-2 px-4 py-2 bg-violet-100 text-violet-700 rounded-full text-sm font-semibold mb-4">
            <Sparkles className="w-4 h-4" />
            Community Spotlight
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
            {communityData.title}
          </h2>
          <p className="text-slate-600 max-w-3xl mx-auto text-lg">
            {communityData.subtitle}
          </p>
        </div>

        {/* Events Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {communityData.events.map((event) => {
            const colors = categoryColors[event.category] || categoryColors.Event;
            
            return (
              <div 
                key={event.id}
                className="bg-white rounded-3xl overflow-hidden shadow-lg border border-slate-100 hover:shadow-xl transition-all duration-300 group"
                data-testid={`community-event-${event.id}`}
              >
                {/* Event Image */}
                <div className={`relative overflow-hidden ${event.imageEs ? 'h-80' : 'h-52'}`}>
                  {event.imageEs ? (
                    <BilingualFlyer imageEn={event.image} imageEs={event.imageEs} alt={event.title} />
                  ) : (
                    <img
                      src={event.image}
                      alt={event.title}
                      className={`w-full h-full object-cover ${event.pdf ? 'object-top' : ''} group-hover:scale-105 transition-transform duration-500`}
                    />
                  )}
                  {/* Category Badge (hidden for Initiative & Community per user request) */}
                  {!['Initiative', 'Community'].includes(event.category) && (
                    <span className={`absolute top-4 left-4 px-3 py-1 ${colors.bg} ${colors.text} rounded-full text-xs font-semibold`}>
                      {event.category}
                    </span>
                  )}
                </div>
                
                {/* Event Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-800 mb-3 group-hover:text-teal-600 transition-colors duration-300">
                    {event.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4 line-clamp-2">
                    {event.description}
                  </p>
                  
                  {/* Date & Location */}
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                      <Calendar className="w-4 h-4 text-teal-500" />
                      <span>{event.date}</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-500 text-sm">
                      <MapPin className="w-4 h-4 text-teal-500" />
                      <span className="truncate">{event.location}</span>
                    </div>
                  </div>
                  
                  {/* Learn More Link */}
                  {event.link && event.link !== '#' && (
                    <a 
                      href={event.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Learn more about ${event.title} (opens in a new tab)`}
                      className="inline-flex items-center gap-2 text-teal-600 font-medium text-sm hover:text-teal-700 transition-colors duration-300"
                    >
                      Learn More
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  )}

                  {/* PDF View / Download */}
                  {event.pdf && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <a
                        href={event.pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View ${event.title} (opens PDF in a new tab)`}
                        data-testid={`spotlight-view-pdf-${event.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-full text-sm font-semibold transition-colors duration-300"
                      >
                        <Eye className="w-4 h-4" />
                        View Memo
                      </a>
                      <a
                        href={event.pdf}
                        download="Under-3-DC-Mayoral-Transition-Memo-Sept-2026.pdf"
                        aria-label={`Download ${event.title} PDF`}
                        data-testid={`spotlight-download-pdf-${event.id}`}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-teal-600 text-teal-700 hover:bg-teal-50 rounded-full text-sm font-semibold transition-colors duration-300"
                      >
                        <Download className="w-4 h-4" />
                        Download
                      </a>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Link 
            to="/community"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 text-white font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-300 group"
            data-testid="view-all-community-btn"
          >
            <Users2 className="w-5 h-5" />
            View All Community Activities
            <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CommunitySpotlightSection;
